import React, { useState, useCallback } from 'react';
import './QuestionZoom.css';

// Wrapper around a single question for the immersive student question view. It:
//   - adds a student zoom control (CSS `zoom`, so zoomed content can scroll);
//   - `immersive` (true for any student question — classroom, standalone quiz,
//     homework, worksheets, Core Skills, lessons) widens/relaxes the media via
//     scoped CSS so the question fills the space;
//   - `fillHeight` passes height:100% through so the lesson's h-full chain
//     (white card -> question -> image) is preserved;
//   - `onExit`, when provided (outside a live call, where there is no nav),
//     renders a top-right "Exit" button so the student can leave.
// Nav-hiding is driven by the question-active state in HomePageV2/Quiz, not here.

const ZOOM_MIN = 0.7;
const ZOOM_MAX = 1.6;
const ZOOM_STEP = 0.1;
const clampZoom = (z) => {
  const rounded = Math.round(z * 10) / 10;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded));
};

export default function QuestionZoom({ children, immersive = false, fillHeight = false, onExit }) {
  const [zoom, setZoom] = useState(1);

  const zoomIn = useCallback(() => setZoom((z) => clampZoom(z + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((z) => clampZoom(z - ZOOM_STEP)), []);
  const resetZoom = useCallback(() => setZoom(1), []);

  const className =
    'question-zoom' +
    (fillHeight ? ' question-zoom--fill-height' : '') +
    (immersive ? ' question-zoom--immersive' : '');

  return (
    <div className={className}>
      {/* Use CSS `zoom` rather than `transform: scale()` so the scaled content
         reflows (its layout box grows), letting the container scroll to reach
         the bottom when zoomed in. transform:scale leaves the layout box at 1x,
         so the overflow is unreachable. */}
      <div
        className="question-zoom__content"
        style={{ zoom: zoom === 1 ? undefined : zoom }}
      >
        {children}
      </div>
      <div className="question-zoom__controls" role="group" aria-label="Zoom question">
        <button
          type="button"
          className="question-zoom__btn"
          onClick={zoomIn}
          disabled={zoom >= ZOOM_MAX}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className="question-zoom__btn question-zoom__btn--reset"
          onClick={resetZoom}
          aria-label="Reset zoom"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          className="question-zoom__btn"
          onClick={zoomOut}
          disabled={zoom <= ZOOM_MIN}
          aria-label="Zoom out"
        >
          −
        </button>
      </div>
      {onExit && (
        <button
          type="button"
          className="question-zoom__exit"
          onClick={onExit}
          aria-label="Exit"
        >
          ✕ Exit
        </button>
      )}
    </div>
  );
}

// Wrap children in QuestionZoom only when enabled (e.g. not the tutor preview).
export const MaybeZoom = ({ enabled, immersive, fillHeight, onExit, children }) =>
  enabled ? (
    <QuestionZoom immersive={immersive} fillHeight={fillHeight} onExit={onExit}>
      {children}
    </QuestionZoom>
  ) : (
    children
  );
